import React from 'react';

// Components
import SecurityPassword from '@/views/containers/settings/SecurityPassword';
import SecurityTwoFactor from '@/views/containers/settings/SecurityTwoFactor';
import { ControlLinkItem, Tabs } from '@/views/components/navigation';

// Components (Legacy)
import LegacySettingsSecurity from '@/views/containers/settings/LegacySettingsSecurity';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router';

// Types
import User from '@/types/user';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';

enum SETTINGS_SECURITY_TABS {
  PASSWORD = 'password',
  TWO_FACTOR = 'two-factor',
}

type Props = RouteComponentProps & {
  user: User;
};

// TODO: Remove these types (Legacy Props and WrapperPros) when the legacy version is removed
type LegacyProps = Props & {
  onUpdatePassword: (current: string, password: string, confirm: string) => Promise<void>;
};

type WrapperProps = Props & LegacyProps;

const SettingsSecurityTabsV2 = (props: Props) => {
  const { location, user } = props;

  const i18nBasePath = 'Settings.Security';
  const navBasePath = '/settings/security/';

  const passwordNavPath = `${navBasePath}${SETTINGS_SECURITY_TABS.PASSWORD}`;
  const twoFactorNavPath = `${navBasePath}${SETTINGS_SECURITY_TABS.TWO_FACTOR}`;

  const getSelectedSettingsSecurityTab = () => {
    switch (location.pathname) {
      case passwordNavPath: {
        return SETTINGS_SECURITY_TABS.PASSWORD;
      }
      case twoFactorNavPath: {
        return SETTINGS_SECURITY_TABS.TWO_FACTOR;
      }
      default: {
        return SETTINGS_SECURITY_TABS.PASSWORD;
      }
    }
  };

  const getSettingsSecurityTabsOptions = () => [
    {
      id: SETTINGS_SECURITY_TABS.PASSWORD,
      value: passwordNavPath,
      label: formatMessage(`${i18nBasePath}.Password.Tab`),
      i18nPath: `${i18nBasePath}.Password.Tab`,
    },
    {
      id: SETTINGS_SECURITY_TABS.TWO_FACTOR,
      value: twoFactorNavPath,
      label: formatMessage(`${i18nBasePath}.TwoFactor.Tab`),
      i18nPath: `${i18nBasePath}.TwoFactor.Tab`,
    },
  ];

  return (
    <div className="settings-security-container">
      <div className="settings-security-header">
        <Tabs className="tabs-v2" selected={getSelectedSettingsSecurityTab()}>
          {getSettingsSecurityTabsOptions().map(option => (
            <ControlLinkItem ariaLabel={option.label} key={option.id} route={option.value || '#'} value={option.id}>
              <FormattedMessage id={option.i18nPath} />
            </ControlLinkItem>
          ))}
        </Tabs>
      </div>
      <PerfectScrollbar className="settings-security-content-psb">
        <Switch>
          <Route path="/settings/security/password">
            <SecurityPassword />
          </Route>

          <Route path="/settings/security/two-factor">
            <SecurityTwoFactor user={user} />
          </Route>

          <Redirect to="/settings/security/password" />
        </Switch>
      </PerfectScrollbar>
    </div>
  );
};

const SettingsSecurityTabs: React.FC<WrapperProps> = props => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <SettingsSecurityTabsV2 {...props} />;
  }

  return <LegacySettingsSecurity {...props} />;
};

export default withRouter(SettingsSecurityTabs);
